import { useCallback, useEffect, useRef, useState } from 'react'
import { db, type Conversation } from '../lib/db'
import { sendCommand } from '../lib/extension'

interface ArchiveUndoConfig {
  allConversationsRef: React.MutableRefObject<Conversation[]>
  setAllConversations: React.Dispatch<React.SetStateAction<Conversation[]>>
  selectedUrn: string | null
  setSelectedUrn: (urn: string | null) => void
  /** Ref to the currently visible (filtered) list — used to find the next item after archive.
   *  A ref (not a value) because the conversations useMemo depends on exitingUrns from this hook. */
  conversationsRef: React.MutableRefObject<Conversation[]>
}

export function useArchiveUndo({
  allConversationsRef,
  setAllConversations,
  selectedUrn,
  setSelectedUrn,
  conversationsRef,
}: ArchiveUndoConfig) {
  const [exitingUrns, setExitingUrns] = useState<Map<string, 'archive' | 'unarchive'>>(new Map())
  // Live mirror for reads inside handleExitComplete, so the old
  // side-effect-inside-updater pattern goes away. Updaters in React must
  // be pure; mutating an outer `intent` variable from inside setState
  // breaks under StrictMode's double-invocation (the side effect runs
  // twice) and is fragile even in production if React ever batches the
  // update to a later tick.
  const exitingUrnsRef = useRef(exitingUrns)
  useEffect(() => {
    exitingUrnsRef.current = exitingUrns
  }, [exitingUrns])
  const [archiveToast, setArchiveToast] = useState<{ urn: string; name: string } | null>(null)
  const archiveToastTimer = useRef<ReturnType<typeof setTimeout>>(undefined)
  const batchUndoRef = useRef<{
    urns: string[]
    previousStates: Map<string, { category: string; isRead: boolean; unreadCount: number }>
  } | null>(null)

  // Cleanup toast timers on unmount
  useEffect(() => {
    return () => {
      if (archiveToastTimer.current) clearTimeout(archiveToastTimer.current)
    }
  }, [])

  // Commit archive: the actual state + IDB + API update (called after animation completes)
  const commitArchive = useCallback((conversationUrn: string) => {
    const conv = allConversationsRef.current.find((c) => c.conversationUrn === conversationUrn)
    if (!conv) return
    setAllConversations((prev) =>
      prev.map((c) =>
        c.conversationUrn === conversationUrn
          ? { ...c, category: 'ARCHIVE', previousCategory: c.category, isRead: true, unreadCount: 0 }
          : c
      )
    )
    db.conversations.update(conversationUrn, { category: 'ARCHIVE', previousCategory: conv.category, isRead: true, unreadCount: 0, _cachedAt: Date.now() })
      .catch((err) => console.warn('[Shell] IndexedDB archive update failed:', err?.message))
    const isSalesNavConv = conv.category === 'SALESNAV'
    sendCommand('ARCHIVE', { conversationUrn, salesNav: isSalesNavConv || undefined }).catch(() => {
      setAllConversations((prev) =>
        prev.map((c) =>
          c.conversationUrn === conversationUrn
            ? { ...c, category: conv.category, isRead: conv.isRead, unreadCount: conv.unreadCount }
            : c
        )
      )
      db.conversations.update(conversationUrn, { category: conv.category, isRead: conv.isRead, unreadCount: conv.unreadCount, _cachedAt: Date.now() })
        .catch((err) => console.warn('[Shell] IndexedDB rollback failed:', err?.message))
    })
  }, [allConversationsRef, setAllConversations])

  // Commit unarchive: the actual state + IDB + API update
  const commitUnarchive = useCallback((conversationUrn: string) => {
    const conv = allConversationsRef.current.find((c) => c.conversationUrn === conversationUrn)
    const restoreCategory = (conv as Conversation & { previousCategory?: string })?.previousCategory
      || (conv?.category === 'SALESNAV' ? 'SALESNAV' : 'INBOX')
    const isSalesNavUnarchive = restoreCategory === 'SALESNAV'
    setAllConversations((prev) =>
      prev.map((c) =>
        c.conversationUrn === conversationUrn
          ? { ...c, category: restoreCategory, previousCategory: undefined }
          : c
      )
    )
    db.conversations.update(conversationUrn, { category: restoreCategory, previousCategory: undefined, _cachedAt: Date.now() })
      .catch((err) => console.warn('[Shell] IndexedDB unarchive update failed:', err?.message))
    sendCommand('UNARCHIVE', { conversationUrn, salesNav: isSalesNavUnarchive || undefined }).catch((err) => {
      console.warn('[Shell] UNARCHIVE failed:', err?.message)
      setAllConversations((prev) =>
        prev.map((c) =>
          c.conversationUrn === conversationUrn
            ? { ...c, category: 'ARCHIVE', previousCategory: restoreCategory }
            : c
        )
      )
      db.conversations.update(conversationUrn, { category: 'ARCHIVE', previousCategory: restoreCategory, _cachedAt: Date.now() })
        .catch((err2) => console.warn('[Shell] IndexedDB rollback failed:', err2?.message))
    })
  }, [allConversationsRef, setAllConversations])

  // Animation-complete callback: commit the actual change.
  // Read intent from the ref (not from inside the setExitingUrns updater),
  // then dispatch a pure update. This keeps the reducer side-effect free.
  const handleExitComplete = useCallback((conversationUrn: string) => {
    const intent = exitingUrnsRef.current.get(conversationUrn)
    setExitingUrns((prev) => {
      if (!prev.has(conversationUrn)) return prev
      const next = new Map(prev)
      next.delete(conversationUrn)
      return next
    })
    if (intent === 'archive') {
      commitArchive(conversationUrn)
    } else if (intent === 'unarchive') {
      commitUnarchive(conversationUrn)
    }
  }, [commitArchive, commitUnarchive])

  // Trigger archive animation (or instant commit as fallback)
  const handleArchive = useCallback((conversationUrn: string) => {
    const conv = allConversationsRef.current.find((c) => c.conversationUrn === conversationUrn)
    if (!conv) return

    const visibleList = conversationsRef.current
    const currentIndex = visibleList.findIndex((c) => c.conversationUrn === conversationUrn)
    const next = visibleList[currentIndex + 1] || visibleList[currentIndex - 1] || null

    if (selectedUrn === conversationUrn) {
      setSelectedUrn(next?.conversationUrn || null)
    }

    // Start exit animation
    setExitingUrns((prev) => new Map(prev).set(conversationUrn, 'archive'))

    if (archiveToastTimer.current) clearTimeout(archiveToastTimer.current)
    setArchiveToast({ urn: conversationUrn, name: conv.participantName || 'Conversation' })
    archiveToastTimer.current = setTimeout(() => setArchiveToast(null), 12_000)
  }, [allConversationsRef, conversationsRef, selectedUrn, setSelectedUrn])

  const handleUnarchive = useCallback((conversationUrn: string) => {
    setExitingUrns((prev) => new Map(prev).set(conversationUrn, 'unarchive'))
    setArchiveToast(null)
  }, [])

  const handleBatchArchive = useCallback((urns: Set<string>) => {
    const count = urns.size
    const urnArray = [...urns]
    const previousStates = new Map<string, { category: string; isRead: boolean; unreadCount: number }>()
    for (const urn of urnArray) {
      const conv = allConversationsRef.current.find((c) => c.conversationUrn === urn)
      if (conv) previousStates.set(urn, { category: conv.category, isRead: conv.isRead, unreadCount: conv.unreadCount })
    }
    setExitingUrns((prev) => {
      const next = new Map(prev)
      for (const urn of urnArray) next.set(urn, 'archive')
      return next
    })
    if (archiveToastTimer.current) clearTimeout(archiveToastTimer.current)
    setArchiveToast({ urn: urnArray[0], name: `${count} conversations` })
    archiveToastTimer.current = setTimeout(() => setArchiveToast(null), 12000)
    batchUndoRef.current = { urns: urnArray, previousStates }
    if (urns.has(selectedUrn || '')) setSelectedUrn(null)
  }, [allConversationsRef, selectedUrn, setSelectedUrn])

  const handleBatchUnarchive = useCallback((urns: Set<string>) => {
    const count = urns.size
    const urnArray = [...urns]
    const previousStates = new Map<string, { category: string; isRead: boolean; unreadCount: number }>()
    for (const urn of urnArray) {
      const conv = allConversationsRef.current.find((c) => c.conversationUrn === urn)
      if (conv) previousStates.set(urn, { category: conv.category, isRead: conv.isRead, unreadCount: conv.unreadCount })
    }
    setExitingUrns((prev) => {
      const next = new Map(prev)
      for (const urn of urnArray) next.set(urn, 'unarchive')
      return next
    })
    if (archiveToastTimer.current) clearTimeout(archiveToastTimer.current)
    setArchiveToast({ urn: urnArray[0], name: `${count} conversations` })
    archiveToastTimer.current = setTimeout(() => setArchiveToast(null), 12000)
    batchUndoRef.current = { urns: urnArray, previousStates }
  }, [allConversationsRef])

  const handleUndoArchive = useCallback(() => {
    setArchiveToast(null)
    if (archiveToastTimer.current) clearTimeout(archiveToastTimer.current)

    const stillAnimating = new Set(exitingUrns.keys())
    setExitingUrns(new Map())

    // Batch undo
    if (batchUndoRef.current) {
      const { urns, previousStates } = batchUndoRef.current
      setAllConversations((prev) =>
        prev.map((c) => {
          const prevState = previousStates.get(c.conversationUrn)
          if (!prevState) return c
          return { ...c, category: prevState.category, isRead: prevState.isRead, unreadCount: prevState.unreadCount }
        })
      )
      const committedUrns = urns.filter((urn) => !stillAnimating.has(urn))
      for (const urn of committedUrns) {
        const prevState = previousStates.get(urn)
        db.conversations.update(urn, { category: prevState?.category || 'INBOX', _cachedAt: Date.now() }).catch(() => {})
      }
      if (committedUrns.length > 0) {
        const chunks: string[][] = []
        for (let i = 0; i < committedUrns.length; i += 5) chunks.push(committedUrns.slice(i, i + 5))
        ;(async () => {
          for (const chunk of chunks) {
            await Promise.allSettled(chunk.map((urn) => sendCommand('UNARCHIVE', { conversationUrn: urn })))
            if (chunks.length > 1) await new Promise((r) => setTimeout(r, 200))
          }
        })()
      }
      batchUndoRef.current = null
      return
    }

    // Single undo
    if (!archiveToast) return
    if (!stillAnimating.has(archiveToast.urn)) {
      commitUnarchive(archiveToast.urn)
    }
    // Only re-select when the unarchived conversation sits in the
    // CURRENT filter list. Between archive and undo, the user may have
    // navigated to a different filter (Starred, a tag, another folder)
    // where this thread doesn't belong; `setSelectedUrn` would then
    // rewrite the URL to `/<wrong-filter>/c/<urn>`, the thread wouldn't
    // render, and the list would show orphaned selection state. Skip
    // the re-select in that case; the conversation still comes back,
    // just without stealing focus. See ADR 0026 single-navigate /
    // filter-matching invariants.
    const currentlyVisible = conversationsRef.current.some(
      (c) => c.conversationUrn === archiveToast.urn,
    )
    if (currentlyVisible) {
      setSelectedUrn(archiveToast.urn)
    }
  }, [archiveToast, commitUnarchive, exitingUrns, setAllConversations, setSelectedUrn, conversationsRef])

  /** Clear the batch undo ref and deselect — call after batch operations that aren't archive. */
  const clearBatchSelection = useCallback(() => {
    batchUndoRef.current = null
  }, [])

  return {
    exitingUrns,
    archiveToast,
    handleArchive,
    handleUnarchive,
    handleBatchArchive,
    handleBatchUnarchive,
    handleExitComplete,
    handleUndoArchive,
    clearBatchSelection,
    /** Dismiss the toast (used when reminder toast overrides it). */
    dismissArchiveToast: useCallback(() => setArchiveToast(null), []),
  }
}
